import React, { useMemo, useState } from 'react';
import { useLinkedCustomers } from '@api/hooks/useVendorPortal';
import { Building2 } from 'lucide-react';
import type { LinkedCustomerResponse } from '@schemas/api';
import { formatDate } from '@utils/formatters';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { TableViewControls } from '@components/common/TableViewControls';
import { TableViewLayout } from '@components/common/TableViewLayout';
import { tableHeaderCellClass, tableHeaderRowClass } from '@components/common/tableViewStyles';

const STATUS_LABELS: Record<string, string> = {
  pending_invitation: 'Pending',
  registered: 'Registered',
  active: 'Active',
  deactivated: 'Deactivated',
};

const STATUS_COLORS: Record<string, string> = {
  pending_invitation: 'bg-yellow-100 text-yellow-800',
  registered: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  deactivated: 'bg-gray-100 text-gray-600',
};

export default function VendorCustomersPage() {
  const { data, isLoading } = useLinkedCustomers();
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'status' | 'linked_since'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const customers = useMemo(() => (data?.items ?? [])
    .filter((customer) => !statusFilter || customer.status === statusFilter)
    .filter((customer) => !searchTerm.trim() || [customer.customer_name, customer.status]
      .join(' ')
      .toLocaleLowerCase()
      .includes(searchTerm.trim().toLocaleLowerCase()))
    .sort((left, right) => {
      const leftValue = sortBy === 'name'
        ? left.customer_name
        : sortBy === 'status'
          ? left.status
          : new Date(left.linked_since).getTime();
      const rightValue = sortBy === 'name'
        ? right.customer_name
        : sortBy === 'status'
          ? right.status
          : new Date(right.linked_since).getTime();
      const comparison = typeof leftValue === 'string'
        ? leftValue.localeCompare(String(rightValue))
        : Number(leftValue) - Number(rightValue);
      return sortOrder === 'asc' ? comparison : -comparison;
    }), [data?.items, searchTerm, sortBy, sortOrder, statusFilter]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-content-primary dark:text-content-primary-dark">
          Connected Customers
        </h1>
        <p className="mt-1 text-content-secondary dark:text-content-secondary-dark">
          Customers who have linked your vendor account.
        </p>
      </div>

      {isLoading ? (
        <div className="text-gray-500 p-6">Loading customers...</div>
      ) : !customers.length ? (
        <div className="text-center py-12 bg-white dark:bg-surface-base-dark rounded-lg border border-border-default">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No customers connected yet.</p>
          <p className="text-sm text-gray-500 mt-1">
            When a customer invites you, they will appear here.
          </p>
        </div>
      ) : (
        <TableViewLayout
          title="Customer connections"
          summary={customers.length ? `Showing 1-${customers.length} of ${customers.length}` : "Showing 0-0 of 0"}
          controls={
            <TableViewControls
              activeFilterCount={Number(Boolean(statusFilter))}
              onClearFilters={() => setStatusFilter('')}
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchLabel="Search customers"
              searchPlaceholder="Search customer names or statuses…"
              filterContent={
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                  <SearchableSelect
                    aria-label="Customer connection status"
                    options={[
                      { id: '', label: 'All statuses' },
                      ...Object.entries(STATUS_LABELS).map(([id, label]) => ({ id, label })),
                    ]}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    clearable={false}
                  />
                </div>
              }
              sortContent={
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Sort by</label>
                    <SearchableSelect
                      aria-label="Sort customers by"
                      options={[
                        { id: 'name', label: 'Customer name' },
                        { id: 'status', label: 'Status' },
                        { id: 'linked_since', label: 'Linked date' },
                      ]}
                      value={sortBy}
                      onChange={(value) => setSortBy(value as typeof sortBy)}
                      clearable={false}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Direction</label>
                    <SearchableSelect
                      aria-label="Customer sort direction"
                      options={[{ id: 'asc', label: 'Ascending' }, { id: 'desc', label: 'Descending' }]}
                      value={sortOrder}
                      onChange={(value) => setSortOrder(value as typeof sortOrder)}
                      clearable={false}
                    />
                  </div>
                </div>
              }
            />
          }
          footer={<div className="px-5 py-3 text-sm text-gray-500">{customers.length} customer{customers.length !== 1 ? 's' : ''}</div>}
        >
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className={tableHeaderRowClass}>
              <tr>
                <th className={`${tableHeaderCellClass} text-left`}>Customer</th>
                <th className={`${tableHeaderCellClass} text-left`}>Status</th>
                <th className={`${tableHeaderCellClass} text-left`}>Linked Since</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {customers.map((customer: LinkedCustomerResponse) => (
                <tr key={customer.link_id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {customer.customer_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[customer.status] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_LABELS[customer.status] || customer.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(customer.linked_since)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableViewLayout>
      )}
    </div>
  );
}
